import React, { useState } from "react";
import { Badge, Tabs, Tab, Card, Row, Col, Button } from "react-bootstrap";
import {
    FaInfoCircle, FaFileInvoiceDollar, FaMoneyBillWave, FaUser, FaBuilding, FaCalendarAlt, FaCheckCircle,
    FaReceipt, FaTimes, FaBoxOpen, FaTruck, FaChevronDown, FaChevronRight, FaShoppingCart, FaIdCard, FaImage
} from "react-icons/fa";
import BrandTable from "../core/components/BrandTable/BrandTable";
import { formatCurrency } from "../../utility";

const PurchaseDetailsCard = ({ data, onClose, hideHeader }) => {
    const [activeTab, setActiveTab] = useState('overview');
    const [expandedRows, setExpandedRows] = useState([]);
    const purchase = data;

    if (!purchase) return null;

    // Toggle row expansion
    const toggleRowExpansion = (rowId) => {
        setExpandedRows(prev =>
            prev.includes(rowId) ? prev.filter(id => id !== rowId) : [...prev, rowId]
        );
    };

    // Items Table Columns
    const itemColumns = [
        {
            key: 'expand',
            label: '',
            width: '50px',
            render: (_, row, index) => (
                <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-primary"
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleRowExpansion(row._id || index);
                    }}
                >
                    {expandedRows.includes(row._id || index) ? (
                        <FaChevronDown size={16} />
                    ) : (
                        <FaChevronRight size={16} />
                    )}
                </Button>
            )
        },
        {
            key: 'itemName',
            label: 'Item',
            render: (val, row) => (
                <div>
                    <div className="fw-bold text-dark">{row.variant_id?.variantName || row.item_id?.itemName || row.itemName || "Unknown Item"}</div>
                    <div className="text-muted x-small">{row.variant_id?.sku || row.variant_id?.barcode || row.item_id?.barcode || row.barcode || "No Barcode"}</div>
                </div>
            )
        },
        {
            key: 'batch_number',
            label: 'Batch',
            render: (val) => <Badge bg="secondary" className="font-monospace small">{val || "N/A"}</Badge>
        },
        {
            key: 'purchaseQty',
            label: 'Qty',
            align: 'center',
            render: (val) => <Badge bg="primary" className="px-3 py-2">{val || 0}</Badge>
        },
        {
            key: 'unit',
            label: 'Unit',
            align: 'center',
            render: (val) => <span className="text-muted small text-uppercase">{val || "pcs"}</span>
        },
        {
            key: 'unitCost',
            label: 'Unit Cost',
            align: 'right',
            render: (val) => <span className="fw-semibold">{formatCurrency(val || 0)}</span>
        },
        {
            key: 'sellingPrice',
            label: 'Selling Price',
            align: 'right',
            render: (val, row) => (
                <div>
                    <div className="fw-bold text-success">{formatCurrency(val || 0)}</div>
                    {row.profitMargin && (
                        <div className="x-small text-muted">
                            <Badge bg="success" className="bg-opacity-25 text-success border-0">
                                +{row.profitMargin}%
                            </Badge>
                        </div>
                    )}
                </div>
            )
        },
        {
            key: 'total_price',
            label: 'Total',
            align: 'right',
            render: (val, row) => <span className="fw-bold text-primary fs-6">{formatCurrency(val || (row.purchaseQty || 0) * (row.unitCost || 0))}</span>
        }
    ];

    // Render expanded row content
    const renderExpandedRow = (item) => (
        <div className="p-4" style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
            <Row className="g-4">
                {/* Cost Breakdown */}
                <Col md={4}>
                    <Card className="border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #696cff' }}>
                        <Card.Body className="p-4">
                            <h6 className="fw-bold mb-3 d-flex align-items-center" style={{ color: '#696cff' }}>
                                <FaMoneyBillWave className="me-2" />
                                Cost Breakdown
                            </h6>
                            <div className="d-flex justify-content-between mb-3 pb-2 border-bottom">
                                <span className="text-muted">Unit Cost:</span>
                                <span className="fw-bold">{formatCurrency(item.unitCost || 0)}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-3 pb-2 border-bottom">
                                <span className="text-muted">Quantity:</span>
                                <Badge bg="primary" className="px-3">{item.purchaseQty || 0}</Badge>
                            </div>
                            <div className="d-flex justify-content-between mb-3 pb-2 border-bottom">
                                <span className="text-muted">Discount:</span>
                                <span className="fw-bold text-danger">{formatCurrency(item.discount || 0)}</span>
                            </div>
                            <div className="d-flex justify-content-between pt-2">
                                <span className="fw-bold fs-6">Total:</span>
                                <span className="fw-bold fs-5" style={{ color: '#696cff' }}>{formatCurrency(item.total_price || 0)}</span>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Pricing & Profit */}
                <Col md={4}>
                    <Card className="border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #71dd37' }}>
                        <Card.Body className="p-4">
                            <h6 className="fw-bold mb-3 d-flex align-items-center text-success">
                                <FaCheckCircle className="me-2" />
                                Pricing & Profit
                            </h6>
                            <div className="d-flex justify-content-between mb-3 pb-2 border-bottom">
                                <span className="text-muted">Selling Price:</span>
                                <span className="fw-bold text-success">{formatCurrency(item.sellingPrice || 0)}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-3 pb-2 border-bottom">
                                <span className="text-muted">Profit Margin:</span>
                                <Badge bg="success" className="px-3">{item.profitMargin || 0}%</Badge>
                            </div>
                            <div className="d-flex justify-content-between mb-3 pb-2 border-bottom">
                                <span className="text-muted">Profit per Unit:</span>
                                <span className="fw-bold text-success">
                                    {formatCurrency((item.sellingPrice || 0) - (item.unitCost || 0))}
                                </span>
                            </div>
                            <div className="d-flex justify-content-between pt-2">
                                <span className="fw-bold fs-6">Total Profit:</span>
                                <span className="fw-bold fs-5 text-success">
                                    {formatCurrency(((item.sellingPrice || 0) - (item.unitCost || 0)) * (item.purchaseQty || 0))}
                                </span>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Specifications */}
                <Col md={4}>
                    <Card className="border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #03c3ec' }}>
                        <Card.Body className="p-4">
                            <h6 className="fw-bold mb-3 d-flex align-items-center text-info">
                                <FaInfoCircle className="me-2" />
                                Specifications
                            </h6>
                            {/* Variant Attributes Dynamic Display */}
                            {item.variant_id?.variantAttributes && item.variant_id.variantAttributes.length > 0 && (
                                <div className="mb-3 pb-2 border-bottom">
                                    {item.variant_id.variantAttributes.map((attr, idx) => (
                                        <div key={idx} className="d-flex justify-content-between mb-1">
                                            <span className="text-muted small text-capitalize">{attr.key}:</span>
                                            <span className="fw-semibold small">{attr.value}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="d-flex justify-content-between mb-3 pb-2 border-bottom">
                                <span className="text-muted">Batch Number:</span>
                                <span className="fw-bold font-monospace small">{item.batch_number || "N/A"}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-3 pb-2 border-bottom">
                                <span className="text-muted">Unit Type:</span>
                                <Badge bg="secondary">{item.unit || "pcs"}</Badge>
                            </div>
                            <div className="d-flex justify-content-between mb-3 pb-2 border-bottom">
                                <span className="text-muted">Condition:</span>
                                <Badge bg={item.condition === "New" ? "success" : "warning"}>
                                    {item.condition || "N/A"}
                                </Badge>
                            </div>
                            <div className="d-flex justify-content-between">
                                <span className="text-muted">Serialized:</span>
                                <Badge bg={item.isSerialized ? "primary" : "secondary"}>
                                    {item.isSerialized ? "Yes" : "No"}
                                </Badge>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Serialized Items List */}
                {item.isSerialized && item.serializedItems && item.serializedItems.length > 0 && (
                    <Col md={12}>
                        <Card className="border-0 shadow-sm">
                            <Card.Body className="p-4">
                                <h6 className="fw-bold mb-3 d-flex align-items-center" style={{ color: '#696cff' }}>
                                    <FaReceipt className="me-2" />
                                    Serialized Items ({item.serializedItems.length})
                                </h6>
                                <div className="table-responsive">
                                    <table className="table table-sm table-hover align-middle mb-0">
                                        <thead style={{ background: '#f8f9fa' }}>
                                            <tr>
                                                <th className="py-3">#</th>
                                                <th className="py-3">Serial Number</th>
                                                <th className="py-3">Condition</th>
                                                <th className="py-3">Battery Health</th>
                                                <th className="py-3 text-end">Unit Cost</th>
                                                <th className="py-3 text-end">Selling Price</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {item.serializedItems.map((serial, idx) => (
                                                <tr key={idx} className="border-bottom">
                                                    <td className="py-3">
                                                        <Badge bg="light" text="dark" className="px-2">{idx + 1}</Badge>
                                                    </td>
                                                    <td className="font-monospace fw-bold py-3">{serial.serialNumber || "N/A"}</td>
                                                    <td className="py-3">
                                                        <Badge bg={serial.condition === "New" ? "success" : "warning"}>
                                                            {serial.condition || "N/A"}
                                                        </Badge>
                                                    </td>
                                                    <td className="py-3">
                                                        {serial.batteryHealth ? (
                                                            <div className="d-flex align-items-center gap-2">
                                                                <div className="progress flex-grow-1" style={{ height: '8px', maxWidth: '100px' }}>
                                                                    <div
                                                                        className={`progress-bar ${serial.batteryHealth >= 80 ? 'bg-success' : serial.batteryHealth >= 50 ? 'bg-warning' : 'bg-danger'}`}
                                                                        style={{ width: `${serial.batteryHealth}%` }}
                                                                    ></div>
                                                                </div>
                                                                <span className="small fw-bold">{serial.batteryHealth}%</span>
                                                            </div>
                                                        ) : <span className="text-muted">N/A</span>}
                                                    </td>
                                                    <td className="text-end py-3 fw-semibold">{formatCurrency(serial.unitCost || item.unitCost || 0)}</td>
                                                    <td className="text-end py-3 fw-bold text-success">{formatCurrency(serial.sellingPrice || item.sellingPrice || 0)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                )}
            </Row>
        </div>
    );

    // Extract items data
    const itemsData = purchase.purchasedItems || purchase.items || [];

    return (
        <div className={hideHeader ? "container-fluid p-0" : "position-fixed top-0 start-0 w-100 h-100 bg-white animate__animated animate__fadeIn"} style={hideHeader ? {} : { zIndex: 10050, overflow: "auto", display: 'flex', flexDirection: 'column' }}>
            <style>
                {`
          .details-label { font-size: 0.8rem; color: #8592a3; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; margin-bottom: 4px; }
          .details-value { font-size: 1.1rem; color: #566a7f; font-weight: 500; }
          .spec-card { background: #f8f9fa; border: 1px solid #e9ecef; border-radius: 12px; padding: 16px; transition: all 0.2s; }
          .spec-card:hover { border-color: #696cff; background: #fff; box-shadow: 0 4px 12px rgba(105, 108, 255, 0.08); }
          .intel-tabs .nav-link {
              padding: 0.25rem 2rem;
              font-weight: 600;
              border: none;
              color: #697a8d;
              display: flex;
              align-items: center;
              gap: 10px;
              transition: all 0.3s;
          }
          .intel-tabs .nav-link.active {
              color: #696cff!important;
              background: #fff!important;
              border-bottom: 3px solid #696cff!important;
              box-shadow: 0 4px 10px rgba(105, 108, 255, 0.1);
          }
          .intel-tabs .nav-link:hover:not(.active) { background: #fdfdff; color: #696cff; }
        `}
            </style>

            {/* Header Section */}
            {!hideHeader && (
                <div className="container-fluid p-0">
                    <div className="row g-0 border-bottom bg-white sticky-top shadow-sm px-4 py-3" style={{ zIndex: 1000 }}>
                        <div className="col-12 d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center gap-3">
                                <h4 className="mb-0 fw-bold">Purchase Details</h4>
                                <Badge bg="primary" className="shadow-sm font-monospace">#{purchase.referenceNumber || purchase.purchase_id || purchase._id}</Badge>
                                <span className="text-muted small">|</span>
                                <Badge bg={purchase.purchase_status === "Approved" || purchase.purchase_status === "Received" || purchase.status === "Approved" ? "success" : "warning"}>
                                    {purchase.purchase_status || purchase.status || "Unknown"}
                                </Badge>
                            </div>

                            <div className="d-flex gap-3 align-items-center">
                                <div className="text-end">
                                    <div className="text-muted x-small fw-bold text-uppercase">Grand Total</div>
                                    <div className="fw-bold text-primary fs-4">{formatCurrency(purchase.grand_total || purchase.amount || 0)}</div>
                                </div>
                                {onClose && (
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="d-flex align-items-center justify-content-center border rounded-circle btn btn-light btn-sm"
                                        style={{ width: "40px", height: "40px" }}
                                        title="Close"
                                    >
                                        <FaTimes size={18} />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="container-fluid p-0">

                <Tabs
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k)}
                    className="intel-tabs px-4 bg-light border-bottom sticky-top"
                    style={{ top: '0px', zIndex: 999 }}
                >
                    <Tab eventKey="overview" title={<><FaInfoCircle /> Overview</>}>
                        <div className="row g-0">
                            {/* Left Panel: Supplier & summary */}
                            <div className="col-12 col-xl-4 bg-light border-end p-5">
                                <Card className="border-0 shadow-sm h-100 mb-4">
                                    <Card.Body className="p-4">
                                        <h5 className="fw-bold mb-4 d-flex align-items-center text-primary">
                                            {purchase.purchase_type === "Trade-In" ? (
                                                <><FaUser className="me-2" /> Customer Information</>
                                            ) : (
                                                <><FaTruck className="me-2" /> Supplier Information</>
                                            )}
                                        </h5>

                                        <div className="mb-4 text-center">
                                            <div className="avatar avatar-xl rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center mx-auto mb-3" style={{ width: 80, height: 80 }}>
                                                {purchase.purchase_type === "Trade-In" ? <FaUser size={30} className="text-primary" /> : <FaBuilding size={30} className="text-primary" />}
                                            </div>
                                            <h5 className="fw-bold">
                                                {purchase.purchase_type === "Trade-In"
                                                    ? (purchase.customer ? `${purchase.customer.first_name} ${purchase.customer.last_name}` : "Unknown Customer")
                                                    : (purchase.supplier?.business_name || purchase.supplier?.name || purchase.supplier || "Walk-in Supplier")
                                                }
                                            </h5>
                                            <p className="text-muted small mb-0">
                                                {purchase.purchase_type === "Trade-In"
                                                    ? (purchase.customer?.email || "No email available")
                                                    : (purchase.supplier?.email || "No email available")
                                                }
                                            </p>
                                        </div>
                                        <hr />

                                        {purchase.purchase_type === "Trade-In" ? (
                                            /* KYC & Customer Metadata */
                                            <div className="kyc-section">
                                                <div className="d-flex justify-content-between mb-3">
                                                    <span className="text-muted">Customer ID</span>
                                                    <span className="fw-semibold">CUS-{purchase.customer?.customer_id || "N/A"}</span>
                                                </div>
                                                <div className="d-flex justify-content-between mb-3">
                                                    <span className="text-muted">Phone</span>
                                                    <span className="fw-semibold">{purchase.customer?.phone_number || "N/A"}</span>
                                                </div>

                                                <div className="bg-light p-3 rounded-3 border border-warning border-opacity-25 mt-4">
                                                    <h6 className="fw-bold mb-3 d-flex align-items-center text-warning" style={{ fontSize: '0.85rem' }}>
                                                        <FaIdCard className="me-2" /> LEGAL IDENTITY (KYC)
                                                    </h6>
                                                    <div className="mb-3">
                                                        <div className="details-label x-small">NIC / ID Number</div>
                                                        <div className="fw-bold text-dark">{purchase.customer?.nic_number || <span className="text-danger">NOT PROVIDED</span>}</div>
                                                    </div>

                                                    <div className="row g-2">
                                                        <div className="col-6">
                                                            <div className="details-label x-small">ID FRONT</div>
                                                            {purchase.customer?.id_front_image ? (
                                                                <a href={purchase.customer.id_front_image} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary w-100 d-flex align-items-center justify-content-center gap-2">
                                                                    <FaImage size={12} /> View
                                                                </a>
                                                            ) : <Badge bg="light" text="muted" className="w-100 border">No Image</Badge>}
                                                        </div>
                                                        <div className="col-6">
                                                            <div className="details-label x-small">ID BACK</div>
                                                            {purchase.customer?.id_back_image ? (
                                                                <a href={purchase.customer.id_back_image} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary w-100 d-flex align-items-center justify-content-center gap-2">
                                                                    <FaImage size={12} /> View
                                                                </a>
                                                            ) : <Badge bg="light" text="muted" className="w-100 border">No Image</Badge>}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            /* Regular Supplier Info */
                                            <>
                                                <div className="d-flex justify-content-between mb-2">
                                                    <span className="text-muted">Contact Person</span>
                                                    <span className="fw-semibold">{purchase.supplier?.contact_person || "N/A"}</span>
                                                </div>
                                                <div className="d-flex justify-content-between mb-2">
                                                    <span className="text-muted">Phone</span>
                                                    <span className="fw-semibold">{purchase.supplier?.phone || purchase.supplier?.contact_number || "N/A"}</span>
                                                </div>
                                            </>
                                        )}
                                    </Card.Body>
                                </Card>

                                <Card className="border-0 shadow-sm">
                                    <Card.Body className="p-4">
                                        <h5 className="fw-bold mb-3 d-flex align-items-center text-success">
                                            <FaMoneyBillWave className="me-2" /> Financial Status
                                        </h5>
                                        <div className="d-flex justify-content-between mb-2">
                                            <span className="text-muted">Transaction Value</span>
                                            <span className="fw-bold">{formatCurrency(purchase.grand_total || purchase.amount || 0)}</span>
                                        </div>
                                        <div className="d-flex justify-content-between mb-2">
                                            <span className="text-muted">Settled Amount</span>
                                            <span className="fw-bold text-success">{formatCurrency(purchase.paid_amount || 0)}</span>
                                        </div>
                                        <div className="d-flex justify-content-between mb-2">
                                            <span className="text-muted">Net Balance</span>
                                            <span className="fw-bold text-danger">{formatCurrency((purchase.grand_total || purchase.amount || 0) - (purchase.paid_amount || 0))}</span>
                                        </div>
                                        <div className="mt-3">
                                            <Badge bg={purchase.payment_status === "Paid" ? "success" : purchase.payment_status === "Partial" ? "warning" : "danger"} className="w-100 py-2">
                                                {purchase.payment_status || "Not Paid"}
                                            </Badge>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </div>

                            {/* Right Panel: Additional Info & Timeline */}
                            <div className="col-12 col-xl-8 p-4 p-md-5 bg-white">
                                <div className="mb-5">
                                    <h5 className="fw-bold mb-4 d-flex align-items-center">
                                        <FaCalendarAlt className="text-info me-2" /> Timeline & References
                                    </h5>
                                    <div className="row g-4">
                                        <Col md={6}>
                                            <div className="spec-card h-100">
                                                <div className="d-flex align-items-center mb-2">
                                                    <FaCalendarAlt className="text-muted me-2" /> <span className="details-label mb-0">Purchase Date</span>
                                                </div>
                                                <div className="details-value">{purchase.purchaseDate ? new Date(purchase.purchaseDate).toLocaleDateString() : "N/A"}</div>
                                                <small className="text-muted">{purchase.purchaseDate ? new Date(purchase.purchaseDate).toLocaleTimeString() : ""}</small>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="spec-card h-100">
                                                <div className="d-flex align-items-center mb-2">
                                                    <FaFileInvoiceDollar className="text-muted me-2" /> <span className="details-label mb-0">Reference No</span>
                                                </div>
                                                <div className="details-value">{purchase.referenceNumber || "N/A"}</div>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="spec-card h-100">
                                                <div className="d-flex align-items-center mb-2">
                                                    <FaReceipt className="text-muted me-2" /> <span className="details-label mb-0">Supplier Bill #</span>
                                                </div>
                                                <div className="details-value">{purchase.supplier_bill_no || "N/A"}</div>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="spec-card h-100 d-flex flex-column justify-content-center">
                                                <div className="details-label x-small">Bill Proof</div>
                                                {purchase.bill_proof ? (
                                                    <a href={purchase.bill_proof} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary d-flex align-items-center justify-content-center gap-2">
                                                        <FaImage size={12} /> View Bill
                                                    </a>
                                                ) : <Badge bg="light" text="muted" className="border">No Receipt</Badge>}
                                            </div>
                                        </Col>
                                    </div>
                                </div>

                                {/* Description Section */}
                                <div className="mt-4">
                                    <h5 className="fw-bold mb-3">Additional Notes</h5>
                                    <div className="p-3 bg-light rounded border">
                                        <p className="text-muted mb-0 lh-base" style={{ fontSize: '1rem', whiteSpace: 'pre-line' }}>
                                            {purchase.additional_notes || purchase.notes || "No specific notes for this purchase."}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Tab>

                    <Tab eventKey="items" title={<><FaBoxOpen /> Purchased Items</>}>
                        <div className="p-4 p-md-5">
                            <BrandTable
                                title={`Items in Purchase ${purchase.referenceNumber || purchase.purchase_id || `#${purchase._id}`}`}
                                icon={<FaShoppingCart />}
                                data={itemsData}
                                columns={itemColumns}
                                emptyMessage="No items listed in this purchase."
                                striped={true}
                                hover={true}
                                expandable={true}
                                renderExpandedRow={renderExpandedRow}
                                expandedRows={expandedRows}
                                onExpandedRowsChange={setExpandedRows}
                                idKey="_id"
                            />
                        </div>
                    </Tab>

                </Tabs>

            </div>
        </div>
    );
};

export default PurchaseDetailsCard;
